import {
    useRef,
    useState
} from 'react'
import { useRestForm } from '@shared/hooks/rest/form'
import { useDialogs } from '@shared/hooks/dialogs'
import { useLeaveConfirmation } from '@shared/hooks/leaveConfirmation'
import { useMethods } from '@shared/hooks/methods'

import * as yup from 'yup'
import axios from '@shared/utils/axios'
import * as Sentry from '@sentry/react'

const imagesSchema = yup.object({
    image: yup.mixed().optional(),
    dep: yup.string().max(20, 'إختر الموجود').oneOf([
        'documents',
        'location',
        'violations'
    ])
})

const attachmentsSchema = yup.object({
    attachment: yup.mixed().optional()
})

const schema = yup.object({
    number: yup.string().required('اكتب هذه الخانة').max(100, 'المكتوب طويل، الحد ١٠٠ حرف'),
    contractor: yup.string().required('اكتب هذه الخانة').max(100, 'المكتوب طويل، الحد ١٠٠ حرف'),
    work_type: yup.string().required('إختر').oneOf([
        'meter_installation',
        'network_connection',
        'emergency_response',
        'asset_replacement',
        'network_upgrade',
        'medium_voltage_operations',
        'projects',
        'assets'
    ], 'إختر الموجود'),
    office: yup.string().nullable().optional(),
    pdc_delegate: yup.string().nullable().optional(),
    district: yup.string().max(100, 'المكتوب طويل، الحد ١٠٠ حرف'),
    is_draft: yup.boolean(),

    // assets case
    equipment_no: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === '' || originalValue === null || originalValue === undefined
                ? undefined
                : value
        )
        .when('work_type', {
            is: 'assets',
            then: schema =>
            schema
                .typeError('اكتب أرقامًا (بالأرقام الإنجليزية)')
                .positive('اكتب موجباً'),
            otherwise: schema => schema.notRequired().strip(),
        }),
    site_type: yup.string().default('cns').when('work_type', {
        is: 'assets',
        then: schema =>
            schema
                .required('إختر')
                .oneOf([
                    'cns',
                    'cds'
                ], 'إختر الموجود'),
        otherwise: schema =>
            schema.strip()
    }),

    // additional / optional
    supervisor_name: yup.string().optional().max(100, 'المكتوب طويل، الحد ١٠٠ حرف'),
    supervisor_signature: yup.mixed().nullable(),

    station_no: yup.string().optional().max(20, 'المكتوب طويل، الحد ٢٠ حرف'),
    notes: yup.string().optional().max(200, 'المكتوب طويل، الحد ١٠٠ حرف'),
    images: yup.array().of(imagesSchema).max(1000, 'الحد ١٠٠٠ صورة'),
    attachments: yup.array().of(attachmentsSchema).max(20, 'الحد ٢٠ ملفات'),
})

const buildFormData = (estimation) => {
    const formData = new FormData()

    Object.entries(estimation).forEach(([key, value]) => {
        if (key === 'images') {
            value.forEach((img, index) => {
                if (Number.isInteger(img.id)) {
                    formData.append(`images[${index}]id`, img.id)
                }
                if ('path' in img) {
                    formData.append(`images[${index}]image`, img.path)
                    formData.append(`images[${index}]dep`, img.dep)
                }
            })
        } else if (key === 'attachments') {
            value.forEach((atch, index) => {
                if (Number.isInteger(atch.id)) {
                    formData.append(`attachments[${index}]id`, atch.id)
                }
                else if ('path' in atch) {
                    formData.append(`attachments[${index}]file`, atch.path)
                }
            })
        } else if (key === 'supervisor_signature' && value) {
            if(value instanceof File) {
                formData.append('supervisor_signature', value)
            } else if (value == '') {
                formData.append('supervisor_signature', '')
            }
        } else if (value !== null && value !== undefined) {
            formData.append(key, value)
        }
    })

    return formData
}

export const useEstimationsForm = (instance) => {
    const { close } = useDialogs()
    const { showMessage } = useMethods()
    const [dialogMode, setDialogMode] = useState('fields') // form | cropper | docs | doc
    const [loading, setLoading] = useState(false)
    const urls = useRef([])

    // form
    const config = { headers: { 'Content-Type': 'multipart/form-data' } }
    const {
        form,
        mutate
    } = useRestForm({
        schema,
        invalidate: [['estimations'], ['quick-look']],
        defaultValues: instance || {
            site_type: 'cns',
            images: [],
            attachments: []
        },

        process: instance
            ? async est => (await axios.patch(`estimations/${est.id}/`, buildFormData(est), config)).data
            : async est => (await axios.post(`estimations/`, buildFormData(est), config)).data,
        onFinish: sup => {
            sup()
            close('estimation')
        }
    })

    useLeaveConfirmation(form.formState.isDirty)

    // crop
    const [toCrop, setToCrop] = useState(null)

    // methods
    const viewFields = () => {
        setDialogMode('fields')
        setTimeout(() => {
            setToCrop(null)
        })
    }

    const viewCropper = e => {
        setToCrop(e.target.src)
        setDialogMode('cropper')
    }

    const viewDocs = () => { setDialogMode('docs') }

    const handleClose = () => {
        if (!form.formState.isDirty) {
            close('estimation')
            urls.current.forEach(url => URL.revokeObjectURL(url))
        } else {
            if (confirm('هل تريد بالتأكيد الإغلاق؟')) {
                close('estimation')
                urls.current.forEach(url => URL.revokeObjectURL(url))
            }
        }
    }

    const handleSave = form.handleSubmit(async (e) => {
        setLoading(true)
        try {
            showMessage({
                text: 'جارٍ التحقق من البيانات...',
                loading: true
            })

            const formData = buildFormData(e)
            try {
                if (instance) {
                    await axios.patch(`estimations/${instance.id}/validate_update/`, formData, config)
                } else {
                    await axios.post(`estimations/validate/`, formData, config)
                }
                showMessage(null)
            } catch (e) {
                showMessage({ text: e?.response?.data?.non_field_errors || 'حدث خطأ اثناء التحقق من صحة البيانات.' })
                return
            }

            showMessage({
                text: 'جارٍ تجهيز الملفات للرفع السحابي...',
                loading: true
            })

            const newImages = e.images?.filter(img => img.file instanceof File) || []
            const newAttachments = e.attachments?.filter(atch => atch.file instanceof File) || []

            const filesToRequest = [
                ...newImages.map(img => ({
                    id: img.id,
                    name: img.file.name,
                    type: img.file.type,
                    size: img.file.size
                })),
                ...newAttachments.map(atch => ({
                    id: atch.id,
                    name: atch.file.name,
                    type: atch.file.type,
                    size: atch.file.size
                }))
            ]

            let uploadedById = {}

            if (filesToRequest.length > 0) {
                let urlsData
                try {
                    const res = await axios.post(
                        'estimations/presigned-urls/',
                        { files: filesToRequest }
                    )
                    urlsData = res.data
                } catch (err) {
                    const detail = err.response?.data?.detail
                    throw new Error(detail || 'فشل تجهيز روابط الرفع')
                }

                const urlMap = Object.fromEntries(urlsData.map(x => [x.original_id, x]))
                const totalFiles = filesToRequest.length
                let completedFiles = 0

                showMessage({
                    text: `جارٍ رفع الملفات... (0 من ${totalFiles})`,
                    loading: true
                })

                const uploadViaPresignedUrl = async (item, urlInfo) => {
                    let response
                    if (import.meta.env.VITE_ENVIRONMENT == 'production') {
                        response = await fetch(urlInfo.upload_url, {
                            method: 'PUT',
                            body: item.file,
                            headers: { 'Content-Type': item.file.type }
                        })

                        if (!response.ok) throw new Error(`Upload failed (${response.status})`)
                    } else {
                        const formData = new FormData()
                        Object.entries(urlInfo.upload_fields || {}).forEach(([key, value]) => formData.append(key, value))
                        formData.append('file', item.file)
                        response = await axios.post(urlInfo.upload_url, formData, config)
                    }
                    completedFiles++
                    showMessage({
                        text: `جارٍ رفع الملفات... (${completedFiles} من ${totalFiles})`,
                        loading: true
                    })

                    const { file, url, ...rest } = item
                    URL.revokeObjectURL(url)
                    return { ...rest, path: urlInfo.path }
                }

                const results = await Promise.all([
                    ...newImages.map(async img => [img.id, await uploadViaPresignedUrl(img, urlMap[img.id])]),
                    ...newAttachments.map(async atch => [atch.id, await uploadViaPresignedUrl(atch, urlMap[atch.id])])
                ])

                uploadedById = Object.fromEntries(results)
            }

            const finalImages = (e.images || []).map(img => uploadedById[img.id] ?? img)
            const finalAttachments = (e.attachments || []).map(atch => uploadedById[atch.id] ?? atch)

            form.setValue('images', finalImages, { shouldDirty: false })
            form.setValue('attachments', finalAttachments, { shouldDirty: false })

            showMessage({
                text: 'جارٍ حفظ المقايسة...',
                loading: true
            })

            mutate({ ...e, images: finalImages, attachments: finalAttachments })
        } catch (error) {
            Sentry.captureException(error, { extra: { context: 'Upload error' } })
            console.log(error)
            showMessage({ text: error.message || 'حدث خطأ أثناء رفع الملفات للسحابة' })
        } finally {
            setLoading(false)
        }
    })

    return {
        form,
        instance,
        dialogMode,
        urls,

        viewCropper,
        viewDocs,
        viewFields,

        toCrop,
        setToCrop,

        handleSave,
        handleClose,

        loading,
        setLoading
    }
}